<template>
  <div class="company-bonuses-page">
    <div class="d-flex align-center mb-4">
      <v-btn variant="text" color="primary" prepend-icon="mdi-arrow-left" @click="$emit('close')">
        {{ t("app.actions.back") }}
      </v-btn>
      <div class="ml-4">
        <div class="text-h6">{{ t("companyFinance.title") }}</div>
        <div class="text-caption text-medium-emphasis">
          {{ companyName || t("companyFinance.unknownCompany") }}
        </div>
      </div>
      <v-spacer></v-spacer>
          <v-btn color="primary" prepend-icon="mdi-gift-outline" class="mr-2" @click="openAddDialog">
            {{ t("companyFinance.actions.addBonus") }}
          </v-btn>
      <v-btn color="secondary" prepend-icon="mdi-file-document-edit-outline" @click="openOrderDialog">
        {{ t("companyFinance.tabs.order") }}
      </v-btn>
    </div>

    <v-dialog v-model="addDialog" max-width="900">
      <v-card>
        <v-card-title class="d-flex align-center">
          {{ t("companyFinance.dialog.addTitle") }}
          <v-spacer></v-spacer>
          <v-btn icon variant="text" @click="closeAddDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="d-flex align-center flex-wrap ga-3 mb-3">
            <v-text-field
              v-model="search"
              :label="t('companyFinance.search')"
              hide-details
              prepend-inner-icon="mdi-magnify"
              class="mr-3"
            ></v-text-field>
            <v-text-field
              v-model="form.pay_date"
              type="date"
              :label="t('companyFinance.fields.payDate')"
              hide-details
              class="mr-3"
            ></v-text-field>
          </div>
          <v-alert v-if="error" type="error" class="mb-3">{{ error }}</v-alert>
          <v-data-table
            :headers="addHeaders"
            :items="filteredEmployees"
            item-value="id"
            class="mb-3"
            density="comfortable"
          >
            <template #header.select>
              <v-checkbox
                hide-details
                density="compact"
                :indeterminate="isSomeSelected && !isAllSelected"
                :model-value="isAllSelected"
                @update:model-value="toggleSelectAll"
              ></v-checkbox>
            </template>
            <template #item.select="{ item }">
              <v-checkbox
                hide-details
                density="compact"
                :model-value="selectedIdsSet.has(item.id)"
                @update:model-value="(val) => toggleSelection(item.id, val)"
              ></v-checkbox>
            </template>
            <template #item.amount="{ item }">
              <v-text-field
                v-model.number="amounts[item.id]"
                type="number"
                min="0"
                density="compact"
                hide-details
              ></v-text-field>
            </template>
            <template #item.amount_words="{ item }">
              <v-text-field
                v-model="amountWords[item.id]"
                :placeholder="t('companyFinance.fields.amountWords')"
                density="compact"
                hide-details
              ></v-text-field>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="closeAddDialog">{{ t("app.actions.cancel") }}</v-btn>
          <v-btn color="primary" :loading="loading" :disabled="!canSave" @click="submitBonuses">
            {{ t("companyFinance.actions.saveBonuses") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="orderDialog" max-width="980">
      <v-card>
        <v-card-title class="d-flex align-center">
          {{ t("companyFinance.actions.generateOrder") }}
          <v-spacer></v-spacer>
          <v-btn icon variant="text" @click="closeOrderDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="d-flex align-center flex-wrap ga-3 mb-3">
            <v-text-field
              v-model="order.order_number"
              :label="t('companyFinance.fields.orderNumber')"
              hide-details
              class="mr-3"
            ></v-text-field>
            <v-text-field
              v-model="order.order_date"
              type="date"
              :label="t('companyFinance.fields.orderDate')"
              hide-details
              class="mr-3"
            ></v-text-field>
            <v-text-field
              v-model="order.city"
              :label="t('companyFinance.fields.city')"
              hide-details
            ></v-text-field>
          </div>
          <v-alert v-if="orderError" type="error" class="mb-3">{{ orderError }}</v-alert>
          <v-data-table
            :headers="orderHeaders"
            :items="orderPayments"
            item-value="id"
            class="mb-3"
            density="comfortable"
          >
            <template #header.select>
              <v-checkbox
                hide-details
                density="compact"
                :indeterminate="orderSomeSelected && !orderAllSelected"
                :model-value="orderAllSelected"
                @update:model-value="toggleSelectAllOrders"
              ></v-checkbox>
            </template>
            <template #item.select="{ item }">
              <v-checkbox
                hide-details
                density="compact"
                :model-value="orderSelectedIdsSet.has(item.id)"
                @update:model-value="(val) => toggleOrderSelection(item.id, val)"
              ></v-checkbox>
            </template>
            <template #item.amount="{ item }">
              {{ formatCurrency(item.amount, item.currency) }}
            </template>
            <template #item.period="{ item }">
              {{ formatPeriod(item.period_start, item.period_end) }}
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="closeOrderDialog">{{ t("app.actions.cancel") }}</v-btn>
          <v-btn color="primary" :loading="orderLoading" :disabled="!canSubmitOrder" @click="submitOrder">
            {{ t("companyFinance.actions.generateOrder") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card outlined>
        <v-card-title class="d-flex align-center">
          <v-icon size="18" class="mr-2">mdi-gift</v-icon>
          {{ t("companyFinance.tableTitle") }}
        </v-card-title>
        <v-data-table
          :headers="allHeaders"
          :items="allPayments"
          :loading="allLoading"
          density="comfortable"
          class="payments-table"
        >
        <template #item.amount="{ item }">
          {{ formatCurrency(item.amount, item.currency) }}
        </template>
        <template #item.period="{ item }">
          {{ formatPeriod(item.period_start, item.period_end) }}
        </template>
        <template #item.pay_date="{ item }">
          {{ formatDate(item.pay_date) }}
        </template>
        <template #item.order="{ item }">
          <v-menu v-if="item.payroll_order_id" location="bottom">
            <template #activator="{ props }">
              <v-btn
                variant="text"
                color="primary"
                v-bind="props"
                class="px-0 text-caption font-weight-medium d-flex align-center"
              >
                <v-icon size="16" class="mr-1">mdi-download</v-icon>
                #{{ item.payroll_order_id }}
              </v-btn>
            </template>
            <v-list density="comfortable" slim>
              <v-list-item @click="downloadOrder(item.payroll_order_id, 'pdf')">
                <v-list-item-title>{{ t("companyFinance.actions.downloadPdf") }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="downloadOrder(item.payroll_order_id, 'docx')">
                <v-list-item-title>{{ t("companyFinance.actions.downloadDoc") }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <span v-else>—</span>
        </template>
        <template #item.actions="{ item }">
          <v-btn
            size="small"
            variant="text"
            color="primary"
            :title="t('app.actions.edit')"
            @click="startEdit(item)"
          >
            <v-icon size="18">mdi-pencil-outline</v-icon>
          </v-btn>
          <v-btn
            size="small"
            variant="text"
            color="error"
            :title="t('app.actions.delete')"
            @click="confirmDelete(item)"
          >
            <v-icon size="18">mdi-delete-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="editDialog.open" max-width="520">
      <v-card>
        <v-card-title>{{ t("companyFinance.dialog.editTitle") }}</v-card-title>
        <v-card-text>
          <v-alert v-if="editDialog.error" type="error" class="mb-3">
            {{ editDialog.error }}
          </v-alert>
          <v-select
            v-model="editDialog.form.payment_type"
            :items="bonusTypeOptions"
            :label="t('companyFinance.table.type')"
            item-title="label"
            item-value="value"
            class="mb-3"
          ></v-select>
          <v-text-field
            v-model.number="editDialog.form.amount"
            type="number"
            :label="t('companyFinance.fields.amount')"
            class="mb-3"
            min="0"
          ></v-text-field>
          <v-textarea
            v-model="editDialog.form.amount_words"
            :label="t('companyFinance.fields.amountWords')"
            rows="2"
            auto-grow
            class="mb-3"
          ></v-textarea>
          <v-text-field
            v-model="editDialog.form.pay_date"
            type="date"
            :label="t('companyFinance.fields.payDate')"
            class="mb-3"
          ></v-text-field>
          <div class="d-flex flex-wrap ga-3">
            <v-text-field
              v-model="editDialog.form.period_start"
              type="date"
              :label="t('companyFinance.fields.periodStart')"
              class="flex-1-1"
            ></v-text-field>
            <v-text-field
              v-model="editDialog.form.period_end"
              type="date"
              :label="t('companyFinance.fields.periodEnd')"
              class="flex-1-1"
            ></v-text-field>
          </div>
          <v-textarea
            v-model="editDialog.form.notes"
            :label="t('companyFinance.fields.notes')"
            rows="2"
            auto-grow
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="closeEdit">{{ t("app.actions.cancel") }}</v-btn>
          <v-btn
            color="primary"
            :loading="editDialog.loading"
            :disabled="!canSubmitEdit"
            @click="submitEdit"
          >
            {{ t("app.actions.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog.open" max-width="420">
      <v-card>
        <v-card-title>{{ t("companyFinance.dialog.deleteTitle") }}</v-card-title>
        <v-card-text>
          <v-alert v-if="deleteDialog.error" type="error" class="mb-3">
            {{ deleteDialog.error }}
          </v-alert>
          <div class="text-body-2">
            {{ t("companyFinance.messages.deleteConfirm", { employee: deleteDialog.employee }) }}
          </div>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="closeDelete">{{ t("app.actions.cancel") }}</v-btn>
          <v-btn
            color="error"
            :loading="deleteDialog.loading"
            @click="submitDelete"
          >
            {{ t("app.actions.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.open" :timeout="3500" color="success">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import { defineComponent, computed, ref, watch, onMounted, reactive, watchEffect } from "vue";
import axios from "axios";
import { useI18n } from "vue-i18n";
import { formatDisplayAmount } from "@/Utile/displayFormat.mjs";

const BONUS_TYPES = new Set([
  "bonus-stimulare-productivitate",
  "bonus-individual",
  "bonus-general",
  "allowance-other",
]);

const parseId = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const normalizeId = (value) => {
  if (!value) return null;
  if (typeof value === "object") {
    return parseId(value.id || value.value);
  }
  return parseId(value);
};
const lastWorkingDayOfMonth = (dateInput) => {
  const base = dateInput ? new Date(dateInput) : new Date();
  if (Number.isNaN(base.getTime())) return "";
  const last = new Date(base.getFullYear(), base.getMonth() + 1, 0);
  while ([0, 6].includes(last.getDay())) {
    last.setDate(last.getDate() - 1);
  }
  const y = last.getFullYear();
  const m = String(last.getMonth() + 1).padStart(2, "0");
  const d = String(last.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export default defineComponent({
  name: "CompanyBonusesPage",
  props: {
    companyId: {
      type: [Number, String],
      required: true,
    },
    companyName: {
      type: String,
      default: "",
    },
    companyCity: {
      type: String,
      default: "",
    },
    employees: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["close", "refresh-employees"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const addDialog = ref(false);
    const orderDialog = ref(false);
    const search = ref("");
    const selectedIdsRaw = ref([]);
    const amounts = reactive({});
    const amountWords = reactive({});
    const loading = ref(false);
    const error = ref("");
    const snackbar = ref({ open: false, message: "" });
    const form = ref({ pay_date: lastWorkingDayOfMonth() });

    const orderPayments = ref([]);
    const selectedPayments = ref([]);
    const orderError = ref("");
    const orderLoading = ref(false);
    const order = ref({
      order_number: "",
      order_date: lastWorkingDayOfMonth(),
      city: props.companyCity || "",
    });

    const editDialog = ref({
      open: false,
      loading: false,
      error: "",
      form: {
        id: null,
        payment_type: "bonus-stimulare-productivitate",
        amount: null,
        amount_words: "",
        pay_date: lastWorkingDayOfMonth(),
        notes: "",
      },
    });
    const deleteDialog = ref({ open: false, loading: false, error: "", id: null, employee: "" });

    const allPayments = ref([]);
    const allLoading = ref(false);

    const addHeaders = computed(() => [
      { title: "", value: "select", sortable: false, width: 50 },
      { title: t("companyFinance.table.employee"), value: "fullName", sortable: false },
      { title: t("companyFinance.table.amount"), value: "amount", sortable: false, width: 150 },
      { title: t("companyFinance.table.amountWords"), value: "amount_words", sortable: false, width: 280 },
    ]);

    const orderHeaders = computed(() => [
      { title: "", value: "select", sortable: false, width: 50 },
      { title: t("companyFinance.table.employee"), value: "employee_name", sortable: false },
      { title: t("companyFinance.table.type"), value: "payment_type", sortable: false },
      { title: t("companyFinance.table.amount"), value: "amount", sortable: false },
      { title: t("companyFinance.table.period"), value: "period", sortable: false },
    ]);

    const allHeaders = computed(() => [
      { title: t("companyFinance.table.employee"), value: "employee_name", sortable: false },
      { title: t("companyFinance.table.type"), value: "payment_type", sortable: false },
      { title: t("companyFinance.table.amount"), value: "amount", sortable: false },
      { title: t("companyFinance.table.period"), value: "period", sortable: false },
      { title: t("companyFinance.fields.payDate"), value: "pay_date", sortable: false },
      { title: t("companyFinance.table.orderId"), value: "order", sortable: false },
      { title: "", value: "actions", sortable: false, width: 120 },
    ]);
    const bonusTypeOptions = computed(() =>
      Array.from(BONUS_TYPES).map((value) => ({
        value,
        label: t(`employeeFinance.types.${value === "bonus-stimulare-productivitate" ? "productivity" : value === "bonus-individual" ? "individual" : value === "bonus-general" ? "general" : "allowance"}`),
      }))
    );

    const employeesWithName = computed(() =>
      (props.employees || []).map((e) => ({
        ...e,
        fullName: [e.first_name, e.last_name].filter(Boolean).join(" "),
      }))
    );
    const filteredEmployees = computed(() => {
      const term = search.value.toLowerCase();
      return employeesWithName.value.filter((e) => !term || e.fullName.toLowerCase().includes(term));
    });

    const selectedIds = computed(() =>
      (selectedIdsRaw.value || [])
        .map((value) => normalizeId(value))
        .filter((id) => Number.isFinite(id) && id > 0)
    );
    const selectedIdsSet = computed(() => new Set(selectedIds.value));

    const canSave = ref(false);
    watchEffect(() => {
      const ids = selectedIds.value;
      const ok =
        ids.length > 0 &&
        ids.every((id) => Number(amounts[id]) > 0) &&
        ids.every((id) => String(amountWords[id] || "").trim().length > 0);
      canSave.value = ok;
    });

    const toggleSelection = (id, checked) => {
      const normalized = normalizeId(id);
      if (!normalized) return;
      const next = new Set(selectedIds.value);
      if (checked) {
        next.add(normalized);
      } else {
        next.delete(normalized);
      }
      selectedIdsRaw.value = Array.from(next);
    };

    const isAllSelected = computed(() => {
      const items = filteredEmployees.value;
      return items.length > 0 && items.every((e) => selectedIdsSet.value.has(e.id));
    });

    const isSomeSelected = computed(() =>
      filteredEmployees.value.some((e) => selectedIdsSet.value.has(e.id))
    );

    const toggleSelectAll = (checked) => {
      if (!checked) {
        selectedIdsRaw.value = [];
        return;
      }
      const allIds = filteredEmployees.value
        .map((e) => normalizeId(e.id))
        .filter((id) => Number.isFinite(id) && id > 0);
      selectedIdsRaw.value = allIds;
    };

    const openAddDialog = () => {
      addDialog.value = true;
      orderDialog.value = false;
    };
    const closeAddDialog = () => {
      addDialog.value = false;
      resetForm();
    };
    const openOrderDialog = () => {
      orderReset();
      orderDialog.value = true;
      addDialog.value = false;
      loadOrderPayments();
    };
    const closeOrderDialog = () => {
      orderDialog.value = false;
      selectedPayments.value = [];
      orderError.value = "";
    };

    const resetForm = () => {
      selectedIdsRaw.value = [];
      Object.keys(amounts).forEach((key) => delete amounts[key]);
      Object.keys(amountWords).forEach((key) => delete amountWords[key]);
      error.value = "";
      loading.value = false;
      form.value = { pay_date: lastWorkingDayOfMonth() };
    };

    const submitBonuses = async () => {
      if (!canSave.value) {
        return;
      }
      loading.value = true;
      error.value = "";
      try {
        const items = selectedIds.value.map((id) => ({
          employee_id: id,
          amount: Number(amounts[id]),
          amount_words: amountWords[id] ? String(amountWords[id]).trim() : null,
          pay_date: form.value.pay_date || undefined,
        }));
        const { data } = await axios.post("/payroll/payments/bulk-bonus", {
          company_id: parseId(props.companyId),
          items,
        });
        const created = Array.isArray(data?.created_ids) ? data.created_ids.length : 0;
        const skipped = Array.isArray(data?.skipped) ? data.skipped.length : 0;

        if (created === 0) {
          error.value =
            skipped > 0
              ? t("companyFinance.messages.bonusDuplicate")
              : t("companyFinance.messages.saveError");
        } else {
          if (skipped > 0) {
            error.value = t("companyFinance.messages.bonusPartial", { created, skipped });
          } else {
            error.value = "";
          }
          snackbar.value = { open: true, message: t("companyFinance.messages.bonusesSaved") };
          resetForm();
          closeAddDialog();
          loadAllPayments();
          emit("refresh-employees");
        }
      } catch (e) {
        error.value = t("companyFinance.messages.saveError");
      } finally {
        loading.value = false;
      }
    };

    const loadOrderPayments = async () => {
      if (!props.companyId) return;
      orderLoading.value = true;
      orderError.value = "";
      try {
        const { data } = await axios.get("/payroll/payments", {
          params: { companyId: parseId(props.companyId), _ts: Date.now() },
        });
        const items = (data?.items || []).filter(
          (p) => BONUS_TYPES.has(p.payment_type) && !p.payroll_order_id
        );
        orderPayments.value = items.map((p) => ({
          ...p,
          employee_name:
            p.first_name || p.last_name
              ? [p.first_name, p.last_name].filter(Boolean).join(" ")
              : t("companyFinance.table.unknownEmployee"),
        }));
        selectedPayments.value = [];
      } catch (e) {
        orderError.value = t("companyFinance.messages.loadError");
      } finally {
        orderLoading.value = false;
      }
    };

    const formatCurrency = (value, currency = "MDL") => {
      if (!Number.isFinite(Number(value))) return "-";
      return `${formatDisplayAmount(value)} ${currency}`;
    };
    const formatDate = (iso) => {
      if (!iso) return "-";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
    };
    const formatPeriod = (start, end) => {
      if (!start || !end) return "-";
      return `${formatDate(start)} - ${formatDate(end)}`;
    };

    const orderSelectedIds = computed(() =>
      (selectedPayments.value || [])
        .map((entry) => normalizeId(entry?.id ?? entry))
        .filter((id) => Number.isFinite(id) && id > 0)
    );
    const orderSelectedIdsSet = computed(() => new Set(orderSelectedIds.value));

    const orderAllSelected = computed(() => {
      const items = orderPayments.value;
      return items.length > 0 && items.every((p) => orderSelectedIdsSet.value.has(p.id));
    });
    const orderSomeSelected = computed(() =>
      orderPayments.value.some((p) => orderSelectedIdsSet.value.has(p.id))
    );

    const toggleOrderSelection = (id, checked) => {
      const normalized = normalizeId(id);
      if (!normalized) return;
      const next = new Set(orderSelectedIds.value);
      if (checked) next.add(normalized);
      else next.delete(normalized);
      selectedPayments.value = orderPayments.value.filter((p) => next.has(p.id));
    };

    const toggleSelectAllOrders = (checked) => {
      if (!checked) {
        selectedPayments.value = [];
        return;
      }
      selectedPayments.value = [...orderPayments.value];
    };

    const canSubmitOrder = computed(() => {
      return (
        orderSelectedIds.value.length > 0 &&
        order.value.order_number &&
        order.value.order_date
      );
    });

    const submitOrder = async () => {
      if (!canSubmitOrder.value) return;
      orderLoading.value = true;
      orderError.value = "";
      try {
        const payload = {
          payment_ids: orderSelectedIds.value,
          order_number: order.value.order_number,
          order_date: order.value.order_date,
          city: order.value.city || undefined,
          company_id: parseId(props.companyId),
        };
        const { data } = await axios.post("/payroll/orders/generate", payload);
        snackbar.value = { open: true, message: t("companyFinance.messages.orderGenerated") };
        loadAllPayments();
        const orderId = data?.order?.id;
        if (orderId) {
          await downloadOrder(orderId, "pdf");
        }
        loadOrderPayments();
        closeOrderDialog();
      } catch (e) {
        const apiError = e?.response?.data?.error;
        orderError.value = apiError || t("companyFinance.messages.orderError");
      } finally {
        orderLoading.value = false;
      }
    };

    const orderReset = () => {
      selectedPayments.value = [];
      orderError.value = "";
      orderLoading.value = false;
      order.value = {
        order_number: "",
        order_date: lastWorkingDayOfMonth(),
        city: props.companyCity || "",
      };
    };

    const loadAllPayments = async () => {
      if (!props.companyId) return;
      allLoading.value = true;
      try {
        const { data } = await axios.get("/payroll/payments", {
          params: { companyId: parseId(props.companyId), _ts: Date.now() },
        });
        const items = (data?.items || []).filter((p) => BONUS_TYPES.has(p.payment_type));
        allPayments.value = items.map((p) => ({
          ...p,
          employee_name:
            p.first_name || p.last_name
              ? [p.first_name, p.last_name].filter(Boolean).join(" ")
              : t("companyFinance.table.unknownEmployee"),
        }));
      } catch (e) {
        allPayments.value = [];
      } finally {
        allLoading.value = false;
      }
    };

    const startEdit = (item) => {
      editDialog.value = {
        open: true,
        loading: false,
        error: "",
        form: {
          id: item?.id || null,
          payment_type: item?.payment_type || "bonus-stimulare-productivitate",
          amount: item?.amount ?? null,
          amount_words: item?.amount_words || "",
          pay_date: item?.pay_date || lastWorkingDayOfMonth(),
          period_start: item?.period_start || null,
          period_end: item?.period_end || null,
          notes: item?.notes || "",
        },
      };
    };

    const closeEdit = () => {
      editDialog.value = {
        open: false,
        loading: false,
        error: "",
        form: {
          id: null,
          payment_type: "bonus-stimulare-productivitate",
          amount: null,
          amount_words: "",
          pay_date: lastWorkingDayOfMonth(),
          period_start: null,
          period_end: null,
          notes: "",
        },
      };
    };

    const canSubmitEdit = computed(() => {
      const f = editDialog.value.form;
      return (
        Number.isFinite(Number(f.amount)) &&
        Number(f.amount) > 0 &&
        Boolean(f.pay_date) &&
        Boolean(f.payment_type)
      );
    });

    const submitEdit = async () => {
      const { form } = editDialog.value;
      if (!form.id || !canSubmitEdit.value) return;
      editDialog.value.loading = true;
      editDialog.value.error = "";
      try {
        await axios.patch(`/payroll/payments/${form.id}`, {
          payment_type: form.payment_type,
          amount: Number(form.amount),
          amount_words: form.amount_words ? String(form.amount_words).trim() : null,
          pay_date: form.pay_date,
          period_start: form.period_start || undefined,
          period_end: form.period_end || undefined,
          notes: form.notes || undefined,
        });
        snackbar.value = { open: true, message: t("companyFinance.messages.updated") };
        closeEdit();
        loadAllPayments();
        loadOrderPayments();
      } catch (e) {
        editDialog.value.error = t("companyFinance.messages.saveError");
      } finally {
        editDialog.value.loading = false;
      }
    };

    const confirmDelete = (item) => {
      deleteDialog.value = {
        open: true,
        loading: false,
        error: "",
        id: item?.id || null,
        employee: item?.employee_name || item?.fullName || "",
      };
    };

    const closeDelete = () => {
      deleteDialog.value = { open: false, loading: false, error: "", id: null, employee: "" };
    };

    const submitDelete = async () => {
      if (!deleteDialog.value.id) return;
      deleteDialog.value.loading = true;
      deleteDialog.value.error = "";
      try {
        await axios.delete(`/payroll/payments/${deleteDialog.value.id}`);
        snackbar.value = { open: true, message: t("companyFinance.messages.deleted") };
        closeDelete();
        loadAllPayments();
        loadOrderPayments();
      } catch (e) {
        deleteDialog.value.error = t("companyFinance.messages.deleteError");
      } finally {
        deleteDialog.value.loading = false;
      }
    };

    const downloadOrder = async (orderId, format = "pdf") => {
      if (!orderId) return;
      const fmt = format === "doc" || format === "docx" ? "docx" : "pdf";
      try {
        const response = await axios.get(`/payroll/orders/${orderId}/download`, {
          params: { format: fmt },
          responseType: "blob",
        });
        const blob = new Blob([response.data], {
          type:
            fmt === "docx"
              ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              : "application/pdf",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `ordin-${orderId}.${fmt}`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        orderError.value = t("companyFinance.messages.orderError");
      }
    };

    watch(
      () => props.companyId,
      (next) => {
        if (next) {
          loadOrderPayments();
          loadAllPayments();
        }
      },
      { immediate: true }
    );

    onMounted(() => {
      loadAllPayments();
    });

    return {
      t,
      addDialog,
      orderDialog,
      openAddDialog,
      closeAddDialog,
      openOrderDialog,
      closeOrderDialog,
      search,
      selectedIdsRaw,
      amounts,
      amountWords,
      filteredEmployees,
      addHeaders,
      loading,
      error,
      submitBonuses,
      resetForm,
      canSave,
      toggleSelection,
      toggleSelectAll,
      isAllSelected,
      isSomeSelected,
      selectedIdsSet,
      form,
      order,
      orderHeaders,
      orderPayments,
      selectedPayments,
      orderSelectedIdsSet,
      orderAllSelected,
      orderSomeSelected,
      toggleOrderSelection,
      toggleSelectAllOrders,
      orderError,
      orderLoading,
      submitOrder,
      canSubmitOrder,
      orderReset,
      formatCurrency,
      formatPeriod,
      formatDate,
      bonusTypeOptions,
      allHeaders,
      allPayments,
      allLoading,
      snackbar,
      startEdit,
      closeEdit,
      editDialog,
      canSubmitEdit,
      submitEdit,
      deleteDialog,
      confirmDelete,
      closeDelete,
      submitDelete,
      downloadOrder,
    };
  },
});
</script>

<style scoped>
.payments-table {
  min-height: 300px;
}
</style>
